import { memo, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import useHttpHook from "../../Hooks/HttpHook";
import { loggedIn } from "../../ReduxSlice/Auth/authSlice";
import { guardLogout } from "../../ReduxSlice/Auth/guardSlice";
import { LoginApi } from "../../utils/ApiConstant";
import InfiniteProgressBar from "../Common/InfiniteProgressBar";
import Header from "./Header";
import EventV2SidebarShell from "./V2/EventV2SidebarShell";
import EventV2PageHead from "./V2/EventV2PageHead";
import EventV2HeroBanner from "./V2/EventV2HeroBanner";
import EventFooter from "./V2/EventFooter";
import { getActiveMenuId } from "./V2/eventMenuConfig";
import {
  getHeroTitleFromHead,
  getEventDocumentTitle,
  resolveEventPageHead,
} from "./V2/eventPageHeadPresets";
import { toast } from "react-toastify";
import { readQueryLoginToken, redirectUnauthorized } from "../../utils/Helper";
import { showErrorToast } from "../../utils/notify";

const Layout = (props) => {
  const location = useLocation();
  const [loading, sendRequest, error] = useHttpHook();
  const [menuOpen, setMenuOpen] = useState(false);
  const dispatch = useDispatch();
  const query = new URLSearchParams(window.location.search);
  const token = readQueryLoginToken(window.location.search);
  // Keep sidebar for guests (e.g. merch → /event); pages opt out via Sidebar={0}.
  const showSidebar = props.Sidebar !== 0;
  const activeMenuId = props.activeMenuId || getActiveMenuId(location.pathname);
  const head = resolveEventPageHead(activeMenuId, props);
  const showHero =
    showSidebar &&
    props.ShowHero !== false &&
    props.HideHero !== true &&
    (props.ShowHero === true || head.showHero === true) &&
    !head.CreateHead &&
    !head.DetailsHead &&
    !(
      head.HeadClassName &&
      /event-create-head|event-checkout-head|event-attendee-head/.test(
        head.HeadClassName
      )
    );
  const heroTitle = getHeroTitleFromHead({
    Heading: head.Heading,
    TitleMark: head.TitleMark,
    activeMenuId,
  });
  // Header + sidebar + main share one viewport; panes scroll independently.
  const useSplitShell = showSidebar;
  const useSplitScroll = showSidebar;

  useEffect(() => {
    document.title = getEventDocumentTitle({
      heading: head.Heading,
      heroTitle,
      activeMenuId,
    });
  }, [head.Heading, heroTitle, activeMenuId, location.pathname]);

  useEffect(() => {
    document.body.classList.add("dashboard-page", "event-home-page");
    if (useSplitShell) {
      document.body.classList.add("event-split-shell");
    }
    return () => {
      document.body.classList.remove(
        "dashboard-page",
        "event-home-page",
        "event-split-shell",
        "event-split-shell--with-hero"
      );
    };
  }, [useSplitShell]);

  useEffect(() => {
    if (!useSplitShell) return undefined;

    const syncHeaderHeight = () => {
      const nav = document.querySelector(".navbar.nav-modern");
      const height = Math.ceil(nav?.getBoundingClientRect().height || 72);
      document.body.style.setProperty("--event-header-height", `${height}px`);
    };

    syncHeaderHeight();
    window.addEventListener("resize", syncHeaderHeight);
    return () => {
      window.removeEventListener("resize", syncHeaderHeight);
      document.body.style.removeProperty("--event-header-height");
    };
  }, [useSplitShell, showHero]);

  useEffect(() => {
    const mainPanel = document.querySelector(
      ".event-main-panel.event-split-scroll"
    );
    const mainBody = document.querySelector(
      ".event-main-panel.event-split-scroll .event-main-body"
    );
    const sideRail = document.querySelector(
      ".event-left-rail.event-split-scroll"
    );
    window.scrollTo(0, 0);
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
    if (mainPanel) mainPanel.scrollTop = 0;
    if (mainBody) mainBody.scrollTop = 0;
    if (sideRail) sideRail.scrollTop = 0;
  }, [location.pathname]);

  useEffect(() => {
    if (error) {
      if (toast.isActive(error) === false) {
        showErrorToast(error);
        if (error === "You are unAuthrised") {
          redirectUnauthorized();
        }
      }
    }
  }, [error]);

  useEffect(() => {
    if (token) {
      login();
    }
  }, [token]);

  if (loading) {
    return <InfiniteProgressBar />;
  }

  const login = async () => {
    try {
      const loginRes = await sendRequest(
        LoginApi,
        { data: { token: token }, method: "post" },
        false
      );
      if (loginRes.status === 1) {
        dispatch(guardLogout());
        localStorage.removeItem("guardData");
        dispatch(loggedIn(loginRes.data));
        localStorage.setItem("userData", JSON.stringify(loginRes.data));
      } else {
        window.location.href = `${process.env.REACT_APP_MERCH_URL}/login`;
      }
    } catch (err) {
      console.log({ err });
      window.location.href = `${process.env.REACT_APP_MERCH_URL}/login`;
    }
  };

  const openMenu = () => {
    setMenuOpen(true);
  };

  return (
    <>
      <Header />
      <main
        className={`dash-wrap dash-v2 event-wrap${
          props.Sidebar === 0 ? " event-page-no-sidebar" : ""
        }${showHero ? " event-wrap--with-hero" : ""}`}
      >
        <div className="container">
          {showHero && (
            <EventV2HeroBanner
              activeId={activeMenuId}
              title={heroTitle}
              subtitle={head.heroSub}
              eyebrow={head.heroEyebrow}
              onOpenMenu={openMenu}
            />
          )}
          <div
            className={`row g-4 mt-1${
              showSidebar ? " event-split-layout" : ""
            }`}
          >
            {showSidebar && (
              <EventV2SidebarShell
                activeId={activeMenuId}
                open={menuOpen}
                onOpenChange={setMenuOpen}
              />
            )}
            <div
              className={
                showSidebar ? "col-lg-9 col-xl-9 event-split-col" : "col-12"
              }
            >
              <section
                id="event-main-scroll"
                className={`dash-panel event-main-panel${
                  showHero ? " event-main-panel-with-hero" : ""
                }${useSplitScroll ? " event-split-scroll" : ""}${
                  head.ExtraclassName ? ` ${head.ExtraclassName}` : ""
                }`}
              >
                <div className="event-main-accent" aria-hidden="true" />
                {(head.Heading ||
                  head.ButtonHeading ||
                  props.extraElement) && (
                  <EventV2PageHead
                    Heading={head.Heading}
                    SubHeading={head.SubHeading}
                    ButtonHeading={head.ButtonHeading}
                    ButtonAction={head.ButtonAction}
                    ButtonIcon={head.ButtonIcon}
                    SecondaryButtonHeading={head.SecondaryButtonHeading}
                    SecondaryButtonAction={head.SecondaryButtonAction}
                    SecondaryButtonIcon={head.SecondaryButtonIcon}
                    extraElement={props.extraElement}
                    BackAction={head.BackAction}
                    HeadClassName={head.HeadClassName}
                    TitleClassName={head.TitleClassName}
                    TitleMark={head.TitleMark}
                    TitleMarkClassName={head.TitleMarkClassName}
                    CreateHead={head.CreateHead}
                    DetailsHead={head.DetailsHead}
                    LeftClassName={head.LeftClassName}
                    HeadLabel={head.HeadLabel}
                    HeadChip={head.HeadChip}
                    hideTitle={showHero && Boolean(head.Heading)}
                  />
                )}
                <div className="event-main-body">{props.children}</div>
              </section>
            </div>
          </div>
        </div>
      </main>
      <EventFooter />
    </>
  );
};

export default memo(Layout);
